import type { ZonedISODateTime } from '@mathflat/shared/@types/date'
import {
  addDays,
  differenceInCalendarISOWeeks,
  getISOWeek,
  getISOWeekYear,
  getWeekOfMonth,
  isAfter,
  isBefore,
  isThursday,
  isWithinInterval,
  set,
  startOfDay,
  subDays,
} from 'date-fns'
import differenceInHours from 'date-fns/differenceInHours'
import { format, getISODay } from 'date-fns/esm'
import getDay from 'date-fns/getDay'
import getMonth from 'date-fns/getMonth'
import startOfMonth from 'date-fns/startOfMonth'

import type {
  DateStr,
  DateTimeStr,
  HourMinuteStr,
  MonthDateStr,
  MonthWeek,
  YearMonthStr,
} from '@/types/common/commonTypes'

class DateUtils {
  korDayOfWeek = ['월', '화', '수', '목', '금', '토', '일'] as const

  toKoDayOfWeek(date: Date) {
    return this.korDayOfWeek[getISODay(date) - 1]
  }

  formatLocalISO(date: Date) {
    return (format(date, 'yyyy-MM-dd') + 'T' + format(date, 'HH:mm:ss')) as DateTimeStr
  }

  formatZonedISO(date: Date): ZonedISODateTime {
    return format(date, "yyyy-MM-dd'T'HH:mm:ssxxx") as ZonedISODateTime
  }

  formatZoned(dateText: string, time: string): ZonedISODateTime {
    return this.formatZonedISO(new Date(`${dateText}T${time}`))
  }

  // yyyy-MM-dd
  toDateString<T extends string>(date: Date, separator: T) {
    return format(date, `yyyy${separator}MM${separator}dd`) as DateStr<T>
  }

  // yy-MM-dd
  toSimpleDateString<T extends string>(date: Date, separator: T) {
    return format(date, `yy${separator}MM${separator}dd`) as DateStr<T>
  }

  // MM-dd
  toMonthDate<T extends string>(date: Date, separator: T) {
    return format(date, `MM${separator}dd`) as MonthDateStr<T>
  }

  // yyyy-MM
  toYearMonth<T extends string>(date: Date, separator: T) {
    return format(date, `yyyy${separator}MM`) as YearMonthStr<T>
  }

  toKoFullDate(date: Date) {
    return format(date, 'yyyy년 MM월 dd일')
  }
  // MM월 dd일
  toKoMonthDay(date: Date) {
    return format(date, 'MM월 dd일')
  }

  toKoMonth(date: Date) {
    return format(date, 'MM월')
  }

  toHourMinute(date: Date) {
    return format(date, 'HH:mm') as HourMinuteStr
  }

  to오늘HourMinute(date: Date, prefix?: string) {
    if (this.isIn24Hours(date)) {
      return `${prefix ?? '오늘'} ${this.toHourMinute(date)}`
    }
    return this.toHourMinute(date)
  }

  toKoFullDateWithIsoWeek(date: Date) {
    const year = format(date, 'yyyy년')
    const month = format(date, 'MM월')

    const firstDayOfMonth = startOfMonth(date)
    const weekOfMonth = differenceInCalendarISOWeeks(date, firstDayOfMonth) + 1

    return `${year} ${month} ${weekOfMonth}주차`
  }

  /**
   * @see https://www.notion.so/freewheelin/T5-3-9a23c176e8654467bb5b9a72066452fc?pvs=4#ca772312c0b7468d8bf564b244570f8c
   */
  toPolicyMonthWeek(date: Date): MonthWeek {
    const firstDayOfWeek = getDay(startOfMonth(date)) // 해당 월 1일의 요일
    const includeFirstWeek = firstDayOfWeek < 5 && firstDayOfWeek > 0 // 첫 주를 n주차 카운팅에 포함할지 여부. 첫 주에 해당 월의 일수가 4일 이상(과반)이면 포함. ex) 1월 첫 주에 1,2,3,4일이 있으면 포함, 1,2,3일만 있으면 과반이 안 되므로 미포함

    // 첫 주를 카운팅에 포함하는 경우
    if (includeFirstWeek) {
      // 기존 셈법과 동일하므로 date 함수를 사용해서 계산 후 return
      return {
        month: getMonth(date),
        week: getWeekOfMonth(date, { weekStartsOn: 1 }),
      }
      // 첫 주를 카운팅에 포함하지 않는 경우
    } else {
      // 계산하려는 날짜가 첫 주에 속하는지 여부
      const isTodayOnFirstWeek = getWeekOfMonth(date, { weekStartsOn: 1 }) === 1

      if (isTodayOnFirstWeek) {
        // 첫 주에 속하는 경우, 서비스 정책상 이전 월의 마지막 주가 되므로 이전 월의 마지막 주가 몇월 몇주차인지 계산
        return this.toPolicyMonthWeek(subDays(startOfMonth(date), 1)) // 전 월의 마지막날을 재탐색 후 반환 (무조건 같은 주임)
      }

      // 첫 주가 아닌 경우, 첫 주를 카운팅하지 않기 때문에 기존 셈법에 1주를 빼고 반환
      return {
        month: getMonth(date),
        week: getWeekOfMonth(date, { weekStartsOn: 1 }) - 1,
      }
    }
  }

  // 지난 24시간 안인지 (isToday랑 다름)
  isIn24Hours(dateToCompare?: Date) {
    if (dateToCompare === undefined) {
      return false
    }

    return differenceInHours(new Date(), dateToCompare) < 24
  }

  fromYearMonthStr<T extends string>(dateStr: YearMonthStr<T>, seperator: T) {
    const [year, month] = dateStr.split(seperator)

    return new Date(Number(year), Number(month) - 1)
  }

  fromDateStr<T extends string>(dateStr: DateStr<T>, seperator: T) {
    const [year, month, date] = dateStr.split(seperator)

    return new Date(Number(year), Number(month) - 1, Number(date))
  }
  setDateByMidnight(amount: number) {
    const addedDate = addDays(new Date(), amount)
    return set(new Date(), {
      year: addedDate.getFullYear(),
      month: addedDate.getMonth(),
      date: addedDate.getDate(),
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    })
  }

  getSameWeekThursday(_date: Date) {
    const date = new Date(_date)
    date.setDate(date.getDate() + 4 - (date.getDay() || 7))

    return date
  }

  getMonthOfYearByISOWeek(date: Date) {
    const thursday = this.getSameWeekThursday(date)
    return thursday.getMonth() + 1
  }

  getWeekOfMonth(date: Date) {
    const thursday = this.getSameWeekThursday(date)

    const sundaysInMonth = this.getWeeklySundays(thursday)

    let weekOfMonth = 1

    for (const sunday of sundaysInMonth) {
      if (new Date(sunday) > thursday) {
        break
      }
      weekOfMonth++
    }

    return weekOfMonth
  }

  // 해당월의 매주 일요일의 날짜를 가져옴
  getWeeklySundays(date: Date) {
    const firstDayOfMonth = startOfMonth(date)
    const firstThursdayOfMonth = firstDayOfMonth

    while (!isThursday(firstThursdayOfMonth)) {
      firstThursdayOfMonth.setDate(firstThursdayOfMonth.getDate() + 1)
    }

    const sundaysInMonth: string[] = []

    while (firstThursdayOfMonth.getMonth() === date.getMonth()) {
      const sunday = addDays(firstThursdayOfMonth, 3)
      sundaysInMonth.push(format(sunday, 'yyyy-MM-dd'))
      firstThursdayOfMonth.setDate(firstThursdayOfMonth.getDate() + 7)
    }

    return sundaysInMonth
  }

  createStringToDate(dateString: DateStr<'-'> | DateTimeStr) {
    const dateTimeRegex = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2}):(\d{2}))?$/
    const match = dateString.match(dateTimeRegex)

    if (!match) {
      throw new Error('Invalid date format')
    }

    const year = Number(match[1])
    const month = Number(match[2]) - 1
    const day = Number(match[3])
    const hour = Number(match[4] || 0)
    const minute = Number(match[5] || 0)
    const second = Number(match[6] || 0)

    return new Date(year, month, day, hour, minute, second)
  }
  // 2주간 노출 등의 정책
  isWithinTwoWeeks(baseDate: Date) {
    const untilDate = addDays(baseDate, 14)
    const now = new Date()
    return isBefore(now, untilDate)
  }

  getActualYearWeeks(year: number) {
    let currentDate = new Date(year, 0, 1)

    // 해당 년도의 첫 주차 찾기
    while (getISOWeekYear(currentDate) < year) {
      currentDate.setDate(currentDate.getDate() + 1)
    }
    const startWeek = getISOWeek(currentDate)

    // 해당 년도의 마지막 주차 찾기
    currentDate = new Date(year, 11, 31)
    while (getISOWeekYear(currentDate) > year) {
      currentDate.setDate(currentDate.getDate() - 1)
    }
    const endWeek = getISOWeek(currentDate)

    return { startWeek, endWeek }
  }

  getThisWeekRangeString() {
    const today = new Date()
    const dayOfWeek = getISODay(today)
    const monday = subDays(today, dayOfWeek - 1)
    const sunday = addDays(monday, 6)

    const formatDate = (d: Date) => `${this.toKoMonthDay(d)} ${this.toKoDayOfWeek(d)}요일`

    return `${formatDate(monday)} ~ ${formatDate(sunday)}`
  }

  checkIsWithin(dateToCheck: Date, startDate: Date, endDate: Date) {
    return isAfter(dateToCheck, startDate) && isBefore(dateToCheck, endDate)
  }

  checkIsWithinInterval(
    dateToCheck: Date,
    options: {
      startDate: Date
      endDate: Date
      strictCheck?: boolean
    },
  ) {
    const { startDate, endDate, strictCheck = false } = options

    if (strictCheck) {
      return isWithinInterval(dateToCheck, {
        start: startDate,
        end: endDate,
      })
    }
    return isWithinInterval(dateToCheck, {
      start: startOfDay(startDate),
      end: startOfDay(endDate),
    })
  }
}

const dateUtils = new DateUtils()

export default dateUtils
